import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SystemVersionDetails.css';
import SystemDetailsSubHeader from '../../components/SystemDetailsSubHeader/SystemDetailsSubHeader';
import { getSystemVersionDetails } from '../../helper/SystemVersionDetailsHelper';
import { setInactiveSystemVersionButton } from '../../modules/actions/action';
import { connect } from 'react-redux';
import moment from 'moment';
import MessageNotification from '../../components/MessageNotification/MessageNotification';
import { ACTIVE_DEVICE, TIMELINE_CONST } from '../../common/constant/common-constants';
import { setEnv } from '../../modules/actions/action';
import { Nx5ReleaseCreate } from '../Nx5Ota/Nx5Release';

const ShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3L4 6v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V6l-8-3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const HistoryIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 8v4l3 2M21 12a9 9 0 1 1-2.64-6.36" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 16V4m0 12 4-4m-4 4-4-4M4 18v2h16v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const OsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="4" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 20h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CodeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M8 8L4 12l4 4M16 8l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChipIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="7" y="7" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 3v4M15 3v4M9 17v4M15 17v4M3 9h4M3 15h4M17 9h4M17 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const getSystemVersion = (version) => version?.softwareVersion || version?.systemVersion || '—';

const formatVersionDate = (version) => {
  const raw = version?.updatedAt || version?.uploadDate || version?.date;
  if (!raw) return '—';
  return moment(raw).format(TIMELINE_CONST.DATE_FORMAT);
};

const getVersionTimestamp = (version) =>
  new Date(version?.updatedAt || version?.uploadDate || version?.date || 0).getTime();

const getHistoryStatus = (version) => {
  if (version?.status) return String(version.status).toUpperCase();
  if (version?.rolledBack || version?.isRolledBack) return 'ROLLED BACK';
  return 'ARCHIVED';
};

const displayVersionValue = (value) => value || '—';

const OsVersionHoverPopupContent = ({ version }) => (
  <>
    <div className="ota-os-popup-header">OS VERSION</div>

    <div className="ota-os-popup-group">
      <span className="ota-os-popup-group-title">SOM</span>
      <div className="ota-os-popup-rows">
        <div className="ota-os-popup-row">
          <span className="ota-os-popup-key">Py App</span>
          <span className="ota-os-popup-val">{displayVersionValue(version?.stmPyAppVersion)}</span>
        </div>
        <div className="ota-os-popup-row">
          <span className="ota-os-popup-key">C App</span>
          <span className="ota-os-popup-val">{displayVersionValue(version?.stmCAppVersion)}</span>
        </div>
        <div className="ota-os-popup-row">
          <span className="ota-os-popup-key">OS</span>
          <span className="ota-os-popup-val">{displayVersionValue(version?.stmOsVersion)}</span>
        </div>
      </div>
    </div>

    <div className="ota-os-popup-group">
      <span className="ota-os-popup-group-title">IOMT</span>
      <div className="ota-os-popup-rows">
        <div className="ota-os-popup-row">
          <span className="ota-os-popup-key">Py App</span>
          <span className="ota-os-popup-val">{displayVersionValue(version?.iomtPyAppVersion)}</span>
        </div>
        <div className="ota-os-popup-row">
          <span className="ota-os-popup-key">J App</span>
          <span className="ota-os-popup-val">{displayVersionValue(version?.iomtJAppVersion)}</span>
        </div>
        <div className="ota-os-popup-row">
          <span className="ota-os-popup-key">OS</span>
          <span className="ota-os-popup-val">{displayVersionValue(version?.iomtOsVersion)}</span>
        </div>
      </div>
    </div>
  </>
);

const computeOsPopupPosition = (anchorRect, popupHeight = 220) => {
  const margin = 8;
  const popupWidth = 240;
  let top = anchorRect.bottom + margin;
  let left = anchorRect.left;

  if (top + popupHeight > window.innerHeight - margin) {
    top = anchorRect.top - popupHeight - margin;
  }
  if (left + popupWidth > window.innerWidth - margin) {
    left = window.innerWidth - popupWidth - margin;
  }

  return {
    top: Math.max(margin, top),
    left: Math.max(margin, left),
  };
};

const OsHoverTrigger = ({ version, className, children }) => {
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const hideTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const height = popupRef.current?.offsetHeight || 220;
    setPosition(computeOsPopupPosition(rect, height));
  }, []);

  const show = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    updatePosition();
    setVisible(true);
  }, [updatePosition]);

  const scheduleHide = useCallback(() => {
    hideTimerRef.current = setTimeout(() => setVisible(false), 120);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  return (
    <>
      <span
        ref={triggerRef}
        className={className}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        onFocus={show}
        onBlur={scheduleHide}
        tabIndex={0}
        role="button"
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={popupRef}
            className="ota-os-hover-popup ota-os-hover-popup--portal"
            style={{ top: position.top, left: position.left }}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            role="tooltip"
          >
            <OsVersionHoverPopupContent version={version} />
          </div>,
          document.body
        )}
    </>
  );
};

const OsHistoryCell = ({ version }) => (
  <td className="ota-history-os-cell">
    <OsHoverTrigger version={version} className="ota-history-os-trigger">
      {displayVersionValue(version?.osVersion)}
    </OsHoverTrigger>
  </td>
);

const OsDetailBox = ({ version }) => (
  <div className="ota-active-detail-box ota-os-detail-box">
    <div className="ota-active-detail-head">
      <OsIcon />
      <span>OS</span>
    </div>
    <div className="ota-active-detail-values">
      <div className="ota-active-detail-row">
        <span className="ota-active-detail-key">VERSION</span>
        <span className="ota-active-detail-val">{displayVersionValue(version?.osVersion)}</span>
      </div>
    </div>

    <div className="ota-os-hover-popup" role="tooltip">
      <OsVersionHoverPopupContent version={version} />
    </div>
  </div>
);

const ActiveVersionCard = ({ version, highlighted }) => (
  <article className={`ota-active-card ${highlighted ? 'ota-active-card--highlighted' : ''}`}>
    <div className="ota-active-card-top">
      <div className="ota-active-card-main ota-version-title-wrap">
        <span className="ota-active-card-label">SYSTEM VERSION</span>
        <h3 className="ota-active-card-version">{getSystemVersion(version)}</h3>
        <div className="ota-version-hover-popup" role="tooltip">
          <div className="ota-os-popup-header">DESCRIPTION</div>
          <p className="ota-version-description">
            {version?.description?.trim() ? version.description : 'No description available.'}
          </p>
        </div>
      </div>
      <div className="ota-active-card-status-wrap">
        <span className="ota-active-badge">
          <span className="ota-active-badge-dot" />
          Active
        </span>
        <span className="ota-active-date">{formatVersionDate(version)}</span>
      </div>
    </div>
    <div className="ota-active-card-details">
      <OsDetailBox version={version} />
      <div className="ota-active-detail-box">
        <div className="ota-active-detail-head">
          <CodeIcon />
          <span>SOFTWARE</span>
        </div>
        <div className="ota-active-detail-values">
          <div className="ota-active-detail-row">
            <span className="ota-active-detail-key">UI</span>
            <span className="ota-active-detail-val">{version?.hmiUiVersion || '—'}</span>
          </div>
          <div className="ota-active-detail-row">
            <span className="ota-active-detail-key">BACKEND</span>
            <span className="ota-active-detail-val">{version?.hmiBackendVersion || '—'}</span>
          </div>
        </div>
      </div>
      <div className="ota-active-detail-box">
        <div className="ota-active-detail-head">
          <ChipIcon />
          <span>FIRMWARE</span>
        </div>
        <div className="ota-active-detail-values">
          <div className="ota-active-detail-row">
            <span className="ota-active-detail-key">MASTER</span>
            <span className="ota-active-detail-val">{version?.fMasterVersion || '—'}</span>
          </div>
          <div className="ota-active-detail-row">
            <span className="ota-active-detail-key">SLAVE</span>
            <span className="ota-active-detail-val">{version?.fSlaveVersion || '—'}</span>
          </div>
        </div>
      </div>
    </div>
  </article>
);

const VENTILATOR_CHANNELS = [
  { value: 'Prod', label: 'Prod' },
  { value: 'Test', label: 'Dev' },
];

const NX5_CHANNELS = [
  { value: 'prod', label: 'Prod' },
  { value: 'test', label: 'Test' },
  { value: 'patch', label: 'Patch' },
];

const SystemVersionDetails = (props) => {
  const { setEnv: storeEnv, activeDevice, showUpdateSystemVersionNotification } = props;
  const isNx5 = activeDevice === ACTIVE_DEVICE.NX5;
  const [dataContainer, setDataContainer] = useState({});
  const [selectedEnv, setSelectedEnv] = useState('Prod');

  useEffect(() => {
    setSelectedEnv(isNx5 ? 'prod' : 'Prod');
  }, [isNx5]);

  useEffect(() => {
    if (isNx5) return;
    storeEnv(selectedEnv);
  }, [selectedEnv, isNx5, storeEnv]);

  useEffect(() => {
    if (isNx5) return;
    handleSystemVersionDataGet();
  }, [isNx5]);

  const handleEnvChange = (env) => {
    setSelectedEnv(env);
  };

  const handleSystemVersionDataGet = () => {
    getSystemVersionDetails()
      .then((res) => {
        setDataContainer(res.data || {});
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const envData = useMemo(() => {
    const data = selectedEnv === 'Prod' ? dataContainer.prod : dataContainer.test;
    return Array.isArray(data) ? data : [];
  }, [dataContainer, selectedEnv]);

  const sortedEnvData = useMemo(
    () => [...envData].sort((a, b) => getVersionTimestamp(b) - getVersionTimestamp(a)),
    [envData]
  );

  const cardVersions = useMemo(() => sortedEnvData.slice(0, 2), [sortedEnvData]);

  const historyVersions = useMemo(() => sortedEnvData.slice(2), [sortedEnvData]);

  return (
    <div className="system-version-details">
      {showUpdateSystemVersionNotification && (
        <div className="ota-upload-notification-wrap">
          <MessageNotification variant="systemVersionUpload" />
        </div>
      )}

      <SystemDetailsSubHeader
        selectedEnv={selectedEnv}
        onEnvChange={handleEnvChange}
        channels={isNx5 ? NX5_CHANNELS : VENTILATOR_CHANNELS}
        showUpload={!isNx5}
        heading={isNx5 ? 'NX5 release' : 'OTA Update Uploads'}
      />

      {isNx5 ? (
        <Nx5ReleaseCreate channel={selectedEnv} />
      ) : (
      <>
      <section className="ota-active-section">
        <div className="ota-section-header">
          <ShieldIcon />
          <h2>Latest Versions</h2>
        </div>
        {cardVersions.length === 0 ? (
          <div className="ota-empty-state">No active versions available.</div>
        ) : (
          <div className="ota-active-grid">
            {cardVersions.map((version, index) => (
              <ActiveVersionCard
                key={`${getSystemVersion(version)}-${index}`}
                version={version}
                highlighted={index === 0}
              />
            ))}
          </div>
        )}
      </section>

      <section className="ota-history-section">
        <div className="ota-section-header ota-section-header--history">
          <div className="ota-section-header-left">
            <HistoryIcon />
            <h2>Version History</h2>
          </div>
          <button type="button" className="ota-filter-btn" aria-label="Filter versions">
            <FilterIcon />
          </button>
        </div>

        <div className="ota-history-table-wrap">
          <table className="ota-history-table">
            <thead>
              <tr>
                <th>SYSTEM</th>
                <th>DATE</th>
                <th>STATUS</th>
                <th>OS</th>
                <th>UI</th>
                <th>BACKEND</th>
                <th>FW MASTER</th>
                <th>FW SLAVE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {historyVersions.length === 0 ? (
                <tr>
                  <td colSpan={9} className="ota-history-empty">No version history available.</td>
                </tr>
              ) : (
                historyVersions.map((version, index) => {
                  const status = getHistoryStatus(version);
                  const isRolledBack = status === 'ROLLED BACK';
                  return (
                    <tr key={`${getSystemVersion(version)}-${index}`}>
                      <td className="ota-history-system">{getSystemVersion(version)}</td>
                      <td>{formatVersionDate(version)}</td>
                      <td>
                        <span className={`ota-history-status ${isRolledBack ? 'ota-history-status--rollback' : 'ota-history-status--archived'}`}>
                          {status}
                        </span>
                      </td>
                      <OsHistoryCell version={version} />
                      <td>{version?.hmiUiVersion || '—'}</td>
                      <td>{version?.hmiBackendVersion || '—'}</td>
                      <td>{version?.fMasterVersion || '—'}</td>
                      <td>{version?.fSlaveVersion || '—'}</td>
                      <td>
                        <button type="button" className="ota-download-btn" aria-label="Download version">
                          <DownloadIcon />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  updateSystemIcon: state.updateSystemIcon.activeUpdateSystemButton,
  showUpdateSystemVersionNotification: state.showSystemVersionUploadNotification.showUploadSystemVersionNotification,
  activeDevice: state.setactiveDevice.device,
});

const mapDispatchToProps = (dispatch) => ({
  enableSystemVersionInactiveButton: () => { dispatch(setInactiveSystemVersionButton(true)); },
  disableSystemVersionInactiveButton: () => { dispatch(setInactiveSystemVersionButton(false)); },
  setEnv: (env) => dispatch(setEnv(env)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SystemVersionDetails);

